import { AnimatePresence, m as motion } from 'framer-motion'
import { AutoAwesome, Login } from '@mui/icons-material'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { Button } from '@dtx-company/design-system/src'
import { Divider } from '@dtx-company/shared-components/src/components/atoms/Divider/index'
import { FC, Fragment, ReactNode, useCallback, useMemo, useState } from 'react'
import {
  LeftNavDropdownMenu,
  LeftNavLink
} from '../../../../components/LeftNavV2/LeftNavPlatformContent.Subcomponents'
import {
  LeftNavDropdownMenuItem,
  PlatformNavIcons
} from '../../../../components/LeftNavV2/LeftNav.types'
import { MarketingPageRoutes, Routes } from '@dtx-company/true-common/src/constants/routes'
import { NAV_HELP_LINK } from '@app/common/src/constants/Nav'
import { NavLink, NavSlice } from '@dtx-company/true-common/src/types/appContent'
import { Next, theme } from '@dtx-company/shared-components/src'
import { NextLinkWrap } from '@app/page/src/components/util/NextLinkUtils'
import { PlanPillV2 } from '../../../../components/layout/header/PlanPill'
import { ProfileImageUpload } from '../../../../components/layout/header/v2/ProfileImageUpload'
import { REDIRECT_TO_CURR_STORE_PAGE_POST_AUTH_ROUTES } from '../../../../components/layout/header/v2/MarketingNavLinks.constants'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { UnstyledButton } from '../../../../components/layout/header/v2/styles'
import { closeMenuIcon, flowcodeNavLogo } from '@app/common/src/constants/assets'
import { createPlansPageUrl } from '../../../../components/pages/Plans/createPlansPageUrl'
import { getNavLinkDataTestType } from '../../../../utils/testing'
import { getTopNavDesktopBreakpoint } from '../TopNav.utils'
import { media } from '@app/common/src/theme'
import { setNavLeftMenu } from '@dtx-company/inter-app/src/redux/slices/config-slice'
import { useAppContent } from '../../../../hooks/useAppContent'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useDispatch } from 'react-redux'
import { useFlowFeature } from '@dtx-company/inter-app/src/hooks/useFlowFeature/useFlowFeature'
import { useGetNavDirectories } from '../../side/side.hooks'
import { useLogout } from '@dtx-company/inter-app/src/hooks/auth/useLogout'
import { useNavigationRoutes } from '../../../../hooks/useNavigationRoutes'
import { useRouter } from 'next/router'
import Image from 'next/image'
import Link from 'next/link'
import events from '@dtx-company/inter-app/src/event-tracking/events/nav'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'

export interface MobileNavMenuProps {
  open?: boolean
  items?: MobileNavMenuItemProps[]
  userIsLoggedInOnMobile?: boolean
}

interface MobileNavMenuItemProps {
  header?: string
  subItems?: MobileNavMenuSubItemProps[]
}

interface MobileNavMenuSubItemProps {
  header: string
  subHeader?: string
  link?: string
}

const dropdownItemHidden = 'dropdownItemHidden'
const dropdownItemShown = 'dropdownItemShown'

const dropdownItemVariants = {
  [dropdownItemHidden]: {
    opacity: 0,
    height: '0px',
    transition: { ease: 'easeOut', duration: 0.25, transitionEnd: { display: 'none' } }
  },
  [dropdownItemShown]: {
    height: '100%',
    opacity: 1,
    transition: { duration: 0.25 }
  }
}

const LinkInnerComponent: FC<{
  item: NavLink
  onClick: () => void
}> = ({ item, onClick }) => {
  const dataTestType = getNavLinkDataTestType(item)
  const TextComponent = (
    <Box flexDirection="column" py="16px" role="button" onClick={onClick}>
      <Text
        variant="header/page"
        fontWeight={600}
        data-testid={`${item.label}-dropdown-${dataTestType}`}
      >
        {item.label}
      </Text>
      <Spacer mt="4px" />
      {item.label_subtext && (
        <Text
          variant="body/small"
          color="primary.grey"
          data-testid={`${item.label_subtext}-dropdown-${dataTestType}-sublabel`}
        >
          {item.label_subtext}
        </Text>
      )}
    </Box>
  )

  if (item.link && item.isExternal) {
    return (
      <a href={item.link} target="_blank" rel="noopener noreferrer">
        {TextComponent}
      </a>
    )
  } else if (item.link) {
    return <Link href={item.link}>{TextComponent}</Link>
  } else {
    return TextComponent
  }
}

interface LinkHeaderProps extends NavSlice {
  open: boolean
}

const LinkHeaderComponent: FC<LinkHeaderProps> = ({ label, link, isExternal, items, open }) => {
  const dataTestType = getNavLinkDataTestType({ label, link, isExternal })

  const LinkText = (
    <Box p="16px 8px">
      <Text
        variant="header/page"
        fontWeight={600}
        data-testid={`${label}-top-level-${dataTestType}`}
      >
        {label}
      </Text>
      {items?.length ? (
        <Box mx="16px" rotation={open ? -90 : 90}>
          <Next color={theme.colors.primary.black} fontSize="10px" />
        </Box>
      ) : null}
    </Box>
  )
  if (isExternal && link) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer">
        {LinkText}
      </a>
    )
  } else if (link) {
    return <Link href={link}>{LinkText}</Link>
  } else {
    return LinkText
  }
}

const MobileNavMenuItem: FC<NavSlice> = props => {
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const setMenuClosed = useCallback(() => dispatch(setNavLeftMenu({ open: false })), [dispatch])

  const { items } = props

  return (
    <Box flexDirection="column">
      <UnstyledButton onClick={() => setOpen(prevState => !prevState)}>
        <LinkHeaderComponent {...props} open={open} />
      </UnstyledButton>
      {items?.length ? (
        <motion.div
          variants={dropdownItemVariants}
          animate={open ? dropdownItemShown : dropdownItemHidden}
          style={{
            flexDirection: 'column',
            height: open ? 'auto' : '0px',
            pointerEvents: open ? 'auto' : 'none'
          }}
        >
          <Box
            p="12px 24px"
            flexDirection="inherit"
            style={{ pointerEvents: open ? 'auto' : 'none' }}
            data-testid={`${props.label}-dropdown`}
          >
            {items.map((item, idx) => (
              <LinkInnerComponent
                item={item}
                onClick={setMenuClosed}
                key={`${item.label}-${idx}`}
              />
            ))}
          </Box>
        </motion.div>
      ) : null}
    </Box>
  )
}

const MobilePlatformNavMenu: FC<MobileNavMenuProps> = props => {
  const { isAuthChecked, isAuthenticated, token, jwt, hasOperation } = useAuthState()
  const [profilePicturesEnabled] = useFlowFeature('platform_products_profile_pictures')
  const firstName = jwt?.firstName ?? ''
  const lastName = jwt?.lastName ?? ''
  const hasDisplayName = firstName.length > 0 && lastName.length > 0
  const displayName = hasDisplayName ? `${firstName} ${lastName}` : 'Me'
  const logout = useLogout()

  const foldersData = useGetNavDirectories(token)

  const platformNavProps = useMemo(() => {
    return {
      foldersData,
      isLoggedIn: isAuthChecked && isAuthenticated
    }
  }, [foldersData, isAuthenticated, isAuthChecked])

  const accountMenuLinkItems: LeftNavDropdownMenuItem[] = useMemo(() => {
    const items: LeftNavDropdownMenuItem[] = [{ text: 'My Account', link: Routes.ACCOUNT }]

    if (hasOperation('view:flowteams')) {
      items.push({ text: 'My Organization', link: Routes.MY_ORG_TEAMS })
    }

    items.push({ text: 'Log Out', action: async () => await logout() })

    return items
  }, [logout, hasOperation])

  const display = props.open ? 'flex' : 'none'
  const dispatch = useDispatch()
  const setMenuClosed = useCallback(() => dispatch(setNavLeftMenu({ open: false })), [dispatch])
  return (
    <Box
      overflow="hidden"
      position="fixed"
      flexDirection="column"
      width="100vw"
      height="100vh"
      zIndex={1001}
      display={[`${display}`, `${display}`, 'none']}
      p="16px"
    >
      <Box width="100%" justifyContent="space-between">
        <Image src={flowcodeNavLogo} alt="logo" width={121} height={28} />
        <Box>
          <Box mr="18px">
            <PlanPillV2 />
          </Box>
          <Image
            role="button"
            src={closeMenuIcon}
            onClick={() => {
              events.Global_Navigation_Closed_MobileNav()
              setMenuClosed()
            }}
            width={21}
            height={21}
            alt=""
          />
        </Box>
      </Box>
      <Spacer mt="12px" />
      <Box overflowY="auto" overflowX="hidden" display="block" width="100%">
        <Spacer mt="32px" />
        <Spacer mt="12px" />
        <Divider width="100%" borderWidth="0px" color={theme.colors.secondary.border} />
        <Spacer mt="12px" />
        <Box px="4px" flexDirection="column">
          <Spacer mt="16px" />
          <Text variant="body/small">Manage</Text>
        </Box>
        <Spacer mt="12px" />
        <LeftNavLink text="Help" link={NAV_HELP_LINK} iconId={PlatformNavIcons.Help} />
        {hasOperation('view:nav_upgrade_link') && (
          <>
            <Spacer mt="6px" />
            <LeftNavLink
              text="Upgrade"
              link={createPlansPageUrl().toString()}
              iconId={PlatformNavIcons.Rocket}
            />
          </>
        )}
        <Spacer mt="6px" />
        <LeftNavDropdownMenu
          items={accountMenuLinkItems}
          header={displayName}
          iconId={PlatformNavIcons.Profile}
          topItem={profilePicturesEnabled ? <ProfileImageUpload /> : undefined}
        />

        {/* Buffer for mobile scroll */}
        <Spacer mt="48px" />
      </Box>
      <Spacer mt="32px" />
    </Box>
  )
}

export const MobileMarketingNavMenu: FC<MobileNavMenuProps> = props => {
  const { t } = useTranslation('nav')
  const display = props.open ? 'flex' : 'none'
  const dispatch = useDispatch()
  const setMenuClosed = useCallback(() => dispatch(setNavLeftMenu({ open: false })), [dispatch])
  const { marketingNav } = useAppContent()
  const router = useRouter()
  const mobileMenuLinks = useMemo(() => {
    return marketingNav.mobile.map((item, index) => ({
      id: `${index}`,
      labelProps: { color: 'primary.white', width: '100%', textAlign: 'center' },
      ...item
    }))
  }, [marketingNav.mobile])

  const redirectToCurrStorePage = REDIRECT_TO_CURR_STORE_PAGE_POST_AUTH_ROUTES.includes(
    router.pathname as Routes
  )
  return (
    <Box
      flexDirection="column"
      width="100vw"
      height="100vh"
      overflowY="auto"
      zIndex={1001}
      display={[`${display}`, `${display}`, `${display}`, 'none']}
      p="16px"
      data-testid="marketing-nav-mobile"
    >
      <Box width="100%" justifyContent="space-between" backgroundColor="primary.white">
        <Image src={flowcodeNavLogo} alt="logo" width={121} height={24} />
        <Image
          role="button"
          src={closeMenuIcon}
          onClick={() => {
            events.Global_Navigation_Closed_MobileNav()
            setMenuClosed()
          }}
          width={21}
          height={21}
          alt=""
        />
      </Box>
      <Spacer mt="36px" />
      <Box
        display="block"
        overflowY="auto"
        overflowX="hidden"
        height="100%"
        flexDirection="inherit"
      >
        <>
          {props.userIsLoggedInOnMobile ? (
            <Box>
              <Button
                size="large"
                fullWidth
                color="tertiary"
                data-testid="mobile-dashboard-button"
                startIcon={Login}
                href={Routes.ANALYTICS_CODES}
                onClick={() => {
                  setMenuClosed()
                  events.Global_MarketingNav_Clicked_DashboardButton()
                }}
              >
                {t('Header.v2.Dashboard')}
              </Button>
            </Box>
          ) : (
            <Box>
              <NextLinkWrap
                flexShrink={0}
                width="50%"
                data-testid="sign-up"
                href={{
                  pathname: Routes.SIGN_UP,
                  query: {
                    ...(redirectToCurrStorePage && {
                      redirectTo: encodeURIComponent(router.asPath)
                    })
                  }
                }}
              >
                <Button
                  size="large"
                  fullWidth
                  color="tertiary"
                  onClick={() => {
                    events.Global_Navigation_MobileNav_Clicked_SignUp()
                    setMenuClosed()
                  }}
                  data-testid="marketing-nav-mobile-signup"
                >
                  {t('Header.v2.SignUpMobile')}
                </Button>
              </NextLinkWrap>
              <Spacer mr="8px" />
              <NextLinkWrap
                flexShrink={0}
                width="50%"
                data-testid="sign-in"
                href={{
                  pathname: Routes.SIGN_IN,
                  query: {
                    ...(!redirectToCurrStorePage && { ...router.query }),
                    ...(redirectToCurrStorePage && {
                      redirectTo: encodeURIComponent(router.asPath)
                    })
                  }
                }}
              >
                <Button
                  fullWidth
                  size="large"
                  color="secondary"
                  onClick={() => {
                    events.Global_Navigation_MobileNav_Clicked_SignIn()
                    setMenuClosed()
                  }}
                  data-testid="marketing-nav-mobile-signin"
                >
                  {t('Header.v2.SignIn')}
                </Button>
              </NextLinkWrap>
            </Box>
          )}
        </>
        <Spacer mb="16px" />
        {mobileMenuLinks?.map(slice => (
          <Fragment key={slice.label}>
            <MobileNavMenuItem {...slice} />
          </Fragment>
        ))}
        <Spacer mt="32px" />
        <Button
          fullWidth
          size="large"
          component="a"
          startIcon={AutoAwesome}
          href={MarketingPageRoutes.CONTACT_SALES}
          onClick={() => {
            events.Global_Navigation_MobileNav_Clicked_ConnectWithUs()
            setMenuClosed()
          }}
        >
          {t('Header.v2.BookADemo')}
        </Button>
      </Box>
    </Box>
  )
}

const MobileAndTabletWrapper = styled(Box)<{ hideBreakpoint: number }>`
  ${({ hideBreakpoint }) =>
    `${media.custom(hideBreakpoint)} {
        display: none;
      }
  `}
`

export const MobileLeftNav: FC<MobileNavMenuProps> = props => {
  const { shouldShowPlatformTopNav, userIsLoggedInOnMobile, currentPageIsPlatformRoute } =
    useNavigationRoutes()
  const showPlatformNav = shouldShowPlatformTopNav && currentPageIsPlatformRoute
  const desktopBreakpoint = getTopNavDesktopBreakpoint(shouldShowPlatformTopNav)

  return (
    <MobileAndTabletWrapper hideBreakpoint={desktopBreakpoint}>
      <MobileLeftNavWrapper open={props.open}>
        {showPlatformNav ? (
          <MobilePlatformNavMenu {...props} />
        ) : (
          <MobileMarketingNavMenu {...props} userIsLoggedInOnMobile={userIsLoggedInOnMobile} />
        )}
      </MobileLeftNavWrapper>
    </MobileAndTabletWrapper>
  )
}

const MobileLeftNavWrapper: FC<{ open?: boolean; children: ReactNode }> = ({ open, children }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ translateX: '-100vw' }}
        exit={{ translateX: '-100vw' }}
        animate={{ translateX: '0px' }}
        transition={{ duration: 0.25 }}
        style={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100001,
          backgroundColor: theme.colors.primary.white,
          width: '100%',
          overflowY: 'hidden',
          maxHeight: '100vh'
        }}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
)
